var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useEffect, useCallback, useState } from 'react';
import { AlphalendClient, getUserPositionCapId, getUserPositionCapIds } from 'alphalend-sdk';
import { getSuiGraphqlUrl, getSuiGrpcOptions } from './rpcEndpoints';
import { alphalendWebSocketService } from '../services/alphalendWebsocket';
// Fetch a single position cap ID for a user
export const fetchUserPositionCapId = (network, address) => __awaiter(void 0, void 0, void 0, function* () {
    if (!address)
        return null;
    try {
        const net = (network || 'mainnet');
        const client = new AlphalendClient(net, getSuiGraphqlUrl(net), getSuiGrpcOptions(net));
        const id = yield getUserPositionCapId(client.blockchain, address);
        return id || null;
    }
    catch (error) {
        console.error('Failed to fetch positionCap ID:', error);
        return null;
    }
});
// Fetch all position cap IDs for a user
export const fetchUserPositionCapIds = (network, address) => __awaiter(void 0, void 0, void 0, function* () {
    if (!address)
        return [];
    try {
        const net = (network || 'mainnet');
        const client = new AlphalendClient(net, getSuiGraphqlUrl(net), getSuiGrpcOptions(net));
        const ids = yield getUserPositionCapIds(client.blockchain, address);
        return ids || [];
    }
    catch (error) {
        console.error('Failed to fetch positionCap IDs:', error);
        return [];
    }
});
// Type guard to check if data is a WebSocketMessage
function isWebSocketMessage(data) {
    return (typeof data === 'object' &&
        data !== null &&
        'event' in data &&
        'data' in data &&
        typeof data.event === 'string');
}
// Get position status based on borrow percentages — pure function, no hook needed
const getPositionStatus = (borrowLimitUsed, safeLimit, liquidationLimit) => {
    if (borrowLimitUsed >= liquidationLimit)
        return { color: '#FD7373', label: 'Liquidatable' };
    if (borrowLimitUsed >= safeLimit)
        return { color: '#FCA066', label: 'Unsafe' };
    return { color: '#49C491', label: 'Safe' };
};
// Persist a status to localStorage, or remove it if null
const persistStatus = (key, status) => {
    if (status) {
        localStorage.setItem(key, JSON.stringify(status));
    }
    else {
        localStorage.removeItem(key);
    }
};
/**
 * Custom hook to manage position counts and statuses for multiple wallets.
 * Handles background fetching and caching of position data for all connected wallets.
 */
export const useMultiWalletPositions = (accountAddresses, currentAccount, currentAccountPositionCapIds, currentAccountPositionsSummary, network) => {
    // Get worst status across multiple positions
    const getWorstStatus = useCallback((positions) => {
        let worstStatus = { color: '#49C491', label: 'Safe' };
        let hasValidPosition = false;
        for (const position of positions) {
            if (!position.totalSupplied || position.totalSupplied <= 0)
                continue;
            if (position.borrowLimitUsedPercentage == null ||
                position.safeLimitPercentage == null ||
                position.liquidationPercentage == null)
                continue;
            hasValidPosition = true;
            const status = getPositionStatus(position.borrowLimitUsedPercentage, position.safeLimitPercentage, position.liquidationPercentage);
            // Priority: Liquidatable > Unsafe > Safe
            if (status.label === 'Liquidatable')
                return status;
            if (status.label === 'Unsafe' && worstStatus.label === 'Safe')
                worstStatus = status;
        }
        return hasValidPosition ? worstStatus : null;
    }, []);
    // Calculate position summary from portfolio data
    const calculatePositionSummaryFromData = useCallback((positionId, data) => {
        const totalSupplied = parseFloat(data.total_supplied_usd || '0');
        const totalBorrowed = parseFloat(data.total_borrowed_usd || '0');
        const netWorth = parseFloat(data.net_worth || '0');
        const borrowLimit = parseFloat(data.safe_borrow_limit || '0');
        const liquidationLimit = parseFloat(data.liquidation_limit || '0');
        const borrowLimitUsed = parseFloat(data.borrow_limit_used || '0');
        const borrowLimitUsedPercentage = totalSupplied > 0 ? (borrowLimitUsed / totalSupplied) * 100 : 0;
        const safeLimitPercentage = totalSupplied > 0 ? (borrowLimit / totalSupplied) * 100 : 0;
        const liquidationPercentage = totalSupplied > 0 ? (liquidationLimit / totalSupplied) * 100 : 0;
        return {
            id: positionId,
            netWorth,
            totalSupplied,
            totalBorrowed,
            borrowLimitUsedPercentage,
            safeLimitPercentage,
            liquidationPercentage,
        };
    }, []);
    // Stable join key — only changes when the actual set of addresses changes
    const addressesKey = accountAddresses.join(',');
    // Wallet position counts
    const [walletPositionCounts, setWalletPositionCounts] = useState(() => {
        const counts = {};
        for (const address of accountAddresses) {
            const stored = localStorage.getItem(`position-count-${address}`);
            if (stored !== null)
                counts[address] = parseInt(stored, 10);
        }
        return counts;
    });
    // Sync counts when the address set changes (wallet connected / disconnected)
    useEffect(() => {
        setWalletPositionCounts((prev) => {
            const counts = {};
            for (const address of accountAddresses) {
                if (prev[address] !== undefined) {
                    counts[address] = prev[address];
                }
                else {
                    const stored = localStorage.getItem(`position-count-${address}`);
                    if (stored !== null)
                        counts[address] = parseInt(stored, 10);
                }
            }
            return counts;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addressesKey]);
    // Keep current account's count in sync with live data
    useEffect(() => {
        if (!currentAccount)
            return;
        const liveCount = currentAccountPositionCapIds.length;
        setWalletPositionCounts((prev) => {
            if (prev[currentAccount] === liveCount)
                return prev;
            return Object.assign(Object.assign({}, prev), { [currentAccount]: liveCount });
        });
        localStorage.setItem(`position-count-${currentAccount}`, String(liveCount));
    }, [currentAccount, currentAccountPositionCapIds.length]);
    // Wallet status map
    const [walletStatusMap, setWalletStatusMap] = useState(() => {
        const statusMap = {};
        for (const address of accountAddresses) {
            const stored = localStorage.getItem(`position-status-${address}`);
            if (stored) {
                try {
                    statusMap[address] = JSON.parse(stored);
                }
                catch (_a) {
                    // ignore corrupt cache
                }
            }
        }
        return statusMap;
    });
    // Sync statuses when the address set changes
    useEffect(() => {
        setWalletStatusMap((prev) => {
            const statusMap = {};
            for (const address of accountAddresses) {
                if (address in prev) {
                    statusMap[address] = prev[address];
                }
                else {
                    const stored = localStorage.getItem(`position-status-${address}`);
                    if (stored) {
                        try {
                            statusMap[address] = JSON.parse(stored);
                        }
                        catch (_a) {
                            statusMap[address] = null;
                        }
                    }
                }
            }
            return statusMap;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addressesKey]);
    // Keep current account's status in sync with live position data
    useEffect(() => {
        if (!currentAccount)
            return;
        const statusKey = `position-status-${currentAccount}`;
        if (currentAccountPositionsSummary.length > 0) {
            const worstStatus = getWorstStatus(currentAccountPositionsSummary);
            setWalletStatusMap((prev) => {
                const existing = prev[currentAccount];
                const next = worstStatus !== null && worstStatus !== void 0 ? worstStatus : null;
                if ((existing === null || existing === void 0 ? void 0 : existing.color) === (next === null || next === void 0 ? void 0 : next.color) && (existing === null || existing === void 0 ? void 0 : existing.label) === (next === null || next === void 0 ? void 0 : next.label))
                    return prev;
                persistStatus(statusKey, next);
                return Object.assign(Object.assign({}, prev), { [currentAccount]: next });
            });
        }
        else {
            setWalletStatusMap((prev) => {
                if (prev[currentAccount] === null)
                    return prev;
                localStorage.removeItem(statusKey);
                return Object.assign(Object.assign({}, prev), { [currentAccount]: null });
            });
        }
    }, [currentAccount, currentAccountPositionsSummary, getWorstStatus]);
    // Other wallet positions
    const [otherWalletPositions, setOtherWalletPositions] = useState({});
    // Resolve a single position's summary via WebSocket cache or subscription
    const fetchPositionSummary = useCallback((positionId) => {
        return new Promise((resolve) => {
            const room = `portfolio:${positionId}`;
            const eventName = `portfolio:${positionId}_high_freq`;
            const cached = alphalendWebSocketService.getCachedEventData(room, eventName);
            if (cached) {
                resolve(calculatePositionSummaryFromData(positionId, cached));
                return;
            }
            let settled = false;
            const timeout = setTimeout(() => {
                if (!settled) {
                    settled = true;
                    alphalendWebSocketService.unsubscribeMessage(room, onMessage);
                    resolve(null);
                }
            }, 5000);
            const onMessage = (data) => {
                if (settled)
                    return;
                if (isWebSocketMessage(data) &&
                    data.event === eventName &&
                    data.data) {
                    settled = true;
                    clearTimeout(timeout);
                    alphalendWebSocketService.unsubscribeMessage(room, onMessage);
                    resolve(calculatePositionSummaryFromData(positionId, data.data));
                }
            };
            alphalendWebSocketService.subscribeMessage(room, onMessage);
        });
    }, [calculatePositionSummaryFromData]);
    // Fetch and update all position data for a single non-current wallet.
    // Clears localStorage cache so fresh data is always fetched.
    const fetchWalletPositionData = useCallback((address) => __awaiter(void 0, void 0, void 0, function* () {
        localStorage.removeItem(`position-count-${address}`);
        localStorage.removeItem(`position-status-${address}`);
        try {
            const positionIds = yield fetchUserPositionCapIds(network, address);
            const count = positionIds.length;
            localStorage.setItem(`position-count-${address}`, String(count));
            setWalletPositionCounts((prev) => {
                if (prev[address] === count)
                    return prev;
                return Object.assign(Object.assign({}, prev), { [address]: count });
            });
            if (count === 0)
                return;
            const summaries = yield Promise.all(positionIds.map((id) => fetchPositionSummary(id !== null && id !== void 0 ? id : '')));
            const valid = summaries.filter((p) => p !== null);
            if (valid.length > 0) {
                setOtherWalletPositions((prev) => (Object.assign(Object.assign({}, prev), { [address]: valid })));
            }
            const worstStatus = getWorstStatus(valid);
            setWalletStatusMap((prev) => {
                const existing = prev[address];
                if ((existing === null || existing === void 0 ? void 0 : existing.color) === (worstStatus === null || worstStatus === void 0 ? void 0 : worstStatus.color) && (existing === null || existing === void 0 ? void 0 : existing.label) === (worstStatus === null || worstStatus === void 0 ? void 0 : worstStatus.label))
                    return prev;
                persistStatus(`position-status-${address}`, worstStatus !== null && worstStatus !== void 0 ? worstStatus : null);
                return Object.assign(Object.assign({}, prev), { [address]: worstStatus !== null && worstStatus !== void 0 ? worstStatus : null });
            });
        }
        catch (error) {
            console.error(`Failed to fetch position data for ${address}:`, error);
        }
    }), [network, getWorstStatus, fetchPositionSummary]);
    // Fetch position data for all non-current wallets in the background
    useEffect(() => {
        if (!addressesKey)
            return;
        const fetch = () => __awaiter(void 0, void 0, void 0, function* () {
            for (const address of accountAddresses) {
                if (address !== currentAccount)
                    yield fetchWalletPositionData(address);
            }
        });
        fetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addressesKey, currentAccount, fetchWalletPositionData]);
    // Call this after creating a position for a non-current wallet to refresh its data immediately
    const refreshOtherWalletPositions = useCallback((address) => {
        void fetchWalletPositionData(address);
    }, [fetchWalletPositionData]);
    return {
        walletPositionCounts,
        walletStatusMap,
        otherWalletPositions,
        refreshOtherWalletPositions,
    };
};
